/* path: src/components/TimeSelector.jsx */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import useAvailableTimes from './Hooks/useAvailableTimes';
import './css/timeSelector.css';
import './css/mobile.css';
import { getShiftForTime } from './Utils/shifts';

const TimeSelector = ({
  guests,
  formData,
  handleChange,
  field,
  selectedDate,
  expanded,
  setCurrentExpandedField,
  restaurantData,
  reservations,
  giftcard, // prop for giftcard selection
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded || false);
  const timeSelectorRef = useRef(null);
  const timeFieldId = (field && field.id) ? field.id : 'time';

  const {
    availableTimes,
    chefsRecommendation,
    isLoading,
    hasRegularTimes,
    hasYeatTimes,
  } = useAvailableTimes({
    selectedDate,
    guests,
    restaurantData,
    reservations,
    giftcard,
  });

  // Determine, per shift, if there is at least one regular (non-waitlist, non-Yeat) slot
  const shiftHasRegular = useMemo(() => {
    const acc = {};
    for (const t of availableTimes) {
      const shift = getShiftForTime(t.value);
      if (shift && !t.isWaitlist && !t.isYeat) {
        acc[shift] = true;
      }
    }
    return acc;
  }, [availableTimes]);

  // Filter out waitlist times when there are other regular alternatives in the same shift
  const displayTimes = useMemo(() => {
    return availableTimes.filter((t) => {
      if (!t.isWaitlist) return true; // always show non-waitlist
      const shift = getShiftForTime(t.value);
      if (!shift) return true; // if shift can't be determined, don't hide
      // hide waitlist if that shift has at least one regular slot
      return !shiftHasRegular[shift];
    });
  }, [availableTimes, shiftHasRegular]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        timeSelectorRef.current &&
        !timeSelectorRef.current.contains(event.target)
      ) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update expanded state when prop changes
  useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded]);

  // Handle time selection
  const handleTimeSelect = (timeValue, isYeatTime = false, isWaitlist = false) => {
    console.log(`Selected time: ${timeValue}${isYeatTime ? ' (Yeat)' : ''}`);
    
    if (isYeatTime) {
      // Find the selected Yeat time to get its lastMinuteId
      const selectedYeatTime = availableTimes.find(time => 
        time.value === timeValue && time.isYeat
      );
      
      if (selectedYeatTime && selectedYeatTime.lastMinuteId) {
        const matezaUrl = `https://tafelboeken.be?lastMinuteId=${selectedYeatTime.lastMinuteId}&restaurantId=${window.restaurantId}&guests=${guests}`;
        console.log(`Redirecting to tafelboeken.be with lastMinuteId: ${selectedYeatTime.lastMinuteId} and guests: ${guests}`);
        window.open(matezaUrl, '_blank');
      } else {
        console.log(`Redirecting to tafelboeken.be for Yeat time selection: ${timeValue} with guests: ${guests} (no lastMinuteId found)`);
        window.open(`https://tafelboeken.be?guests=${guests}`, '_blank');
      }
      // Ensure waitlist flag is unset for Yeat selection
      handleChange({ target: { name: 'waitlist', value: false } });
      return;
    }
    
    // Regular time selection
    window.selectedTime = timeValue;

    handleChange({
      target: { name: timeFieldId, value: timeValue },
    });
    // Store waitlist flag alongside time selection
    handleChange({ target: { name: 'waitlist', value: !!isWaitlist } });
    
    setIsExpanded(false);
    
    if (setCurrentExpandedField) {
      setCurrentExpandedField(null);
    }
  };

  // Format the displayed time
  const formatDisplayTime = () => {
    if (formData[timeFieldId]) {
      const selected = availableTimes.find(time => time.value === formData[timeFieldId]);
      return selected ? selected.label : 'Selecteer een tijd';
    }
    return 'Selecteer een tijd';
  };

  if (!field) {
    return null;
  }

  return (
    <div className="form-group time-selector-container" ref={timeSelectorRef}>
      <label className="default-text-color" htmlFor={field.id}>
        {field.label}
      </label>

      {!selectedDate ? (
        <p className="info-text">Selecteer eerst een datum.</p>
      ) : (
        <>
          <div
            className="time-display"
            onClick={() => {
              setIsExpanded(!isExpanded);
              if (!isExpanded && setCurrentExpandedField) {
                setCurrentExpandedField('time');
              }
            }}
          >
            <span>{formatDisplayTime()}</span>
            <span className="arrow">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <path
                  d="M7 10l5 5 5-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </span>
          </div>
          {isExpanded && (
            <div className="time-selector">
              {isLoading ? (
                <div className="loading-times">Beschikbaarheid controleren...</div>
              ) : displayTimes.length === 0 ? (
                <div className="no-times">Geen beschikbare tijden voor {guests} personen.</div>
              ) : (
                <div className="time-options">
                  {displayTimes.map((time) => {
                    const isWaitlist = !!time.isWaitlist;
                    return (
                      <div
                        key={time.value}
                        className={`time-option ${
                          formData[timeFieldId] === time.value ? 'selected' : ''
                        } ${time.value === chefsRecommendation ? 'chefs-recommendation' : ''} ${
                          time.isYeat ? 'yeat-time' : ''
                        } ${isWaitlist ? 'waitlist-time' : ''}`}
                        onClick={() => handleTimeSelect(time.value, time.isYeat, isWaitlist)}
                        title={isWaitlist ? 'Wachtlijst' : undefined}
                      >
                        {time.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Show chef's recommendation info only when regular times are available */}
          {chefsRecommendation && hasRegularTimes && (
            <p className="chef-recommendation-info">
              Kies voor de Chef's Aanrader om piekuren te vermijden
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default TimeSelector;
